import type { CustomRoleUserUpdateMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export type UpdateCustomRolesTRPCInput = {
  userId: string;
  customRoleIds: string[];
};

type TrpcUpdateCustomRolesResponse = {
  affected_rows: number;
};

const mapTrpcResponseToGraphQL = (
  trpcData: TrpcUpdateCustomRolesResponse
): CustomRoleUserUpdateMutation => ({
  customRoleUserUpdate: {
    affected_rows: trpcData.affected_rows,
  },
});

export const useUpdateCustomRolesTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.user.updateCustomRoles.mutationOptions({
      onSuccess: async () => {
        // Invalidate users list and per-user read paths so the modal and
        // settings table reflect the updated custom-role membership.
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.user.users.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.user.userById.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.user.userByIdWithRoles.queryKey(),
          }),
        ]);
      },
    }),
  });

  return {
    updateCustomRoles: async (
      variables: UpdateCustomRolesTRPCInput
    ): Promise<CustomRoleUserUpdateMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
